import { useTranslation } from 'react-i18next';
import { ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { FeatureRequest } from '@/hooks/useFeatureRequests';
import { isVotingOpen } from './roadmapGroups';

const categoryKeys: Record<string, string> = {
  'feed-management': 'feed_management',
  optimization: 'optimization',
  analytics: 'analytics',
  integrations: 'integrations',
  'ux-ui': 'ux_ui',
  other: 'other',
};

const statusKeys: Record<string, string> = {
  under_review: 'under_review',
  planned: 'planned',
  in_progress: 'in_progress',
  completed: 'completed',
  declined: 'declined',
};

const statusVariants: Record<string, string> = {
  under_review: 'bg-muted text-muted-foreground',
  planned: 'bg-accent text-primary-ink',
  in_progress: 'bg-warning-light text-warning-ink',
  completed: 'bg-success-light text-success-ink',
  declined: 'bg-destructive-light text-destructive-ink',
};

interface FeatureRequestCardProps {
  request: FeatureRequest;
  hasVoted: boolean;
  onToggleVote: (requestId: string) => void;
  isVoting: boolean;
}

export function FeatureRequestCard({ request, hasVoted, onToggleVote, isVoting }: FeatureRequestCardProps) {
  const { t, i18n } = useTranslation('roadmap');
  // Die Web-Roadmap lokalisiert ueber get-public-roadmap; die App rendert die
  // Zeile selbst und muss die deutsche Fassung deshalb hier waehlen. Fehlt sie
  // (nutzererzeugte Anfragen haben nur EN), faellt die Karte auf EN zurueck.
  const isGerman = (i18n.language ?? '').toLowerCase().startsWith('de');
  const title = isGerman && request.title_de ? request.title_de : request.title;
  const description = isGerman && request.description_de ? request.description_de : request.description;
  // A7O-18 (Option 4A): dieselbe Regel fuer die Antwort des Teams.
  const adminResponse = isGerman && request.admin_response_de ? request.admin_response_de : request.admin_response;
  // A7O-13: der Knopf hatte als Namen nur die Zahl und keinen Zustand.
  // Ueber Erledigtes und Abgelehntes wird nicht mehr abgestimmt (A7O-11).
  const votingOpen = isVotingOpen(request.status);

  return (
    <Card className="group">
      <CardContent className="flex gap-4 p-4">
        {/* Vote Button */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            className={`h-12 w-12 flex-col gap-0 p-0 ${
              hasVoted && votingOpen
                ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                : votingOpen
                  ? 'hover:border-primary hover:text-primary'
                  : ''
            }`}
            aria-label={
              votingOpen
                ? t('features.voteAria', { title, count: request.vote_count })
                : t('features.voteClosedAria', { title, count: request.vote_count })
            }
            aria-pressed={votingOpen ? hasVoted : undefined}
            onClick={() => {
              if (votingOpen) onToggleVote(request.id);
            }}
            disabled={isVoting || !votingOpen}
          >
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
            <span className="text-xs font-semibold">{request.vote_count}</span>
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 mb-1">
            <h3 className="font-medium text-sm leading-tight">{title}</h3>
          </div>
          {description && (
            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
              {description}
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {t(`features.categories.${categoryKeys[request.category] || request.category}`)}
            </Badge>
            <span className={`inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-medium ${statusVariants[request.status] || ''}`}>
              {t(`features.statuses.${statusKeys[request.status] || request.status}`)}
            </span>
          </div>
          {adminResponse && (
            <div className="mt-2 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
              <span className="font-medium">{t('features.adminResponse')} </span>{adminResponse}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
